import type { GeoJSONFeatureCollection } from "@mm/api.meteomatics.com";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import type { Position } from "geojson";
import type { Expression } from "mapbox-gl";

/**
 * Color ramp expression for jet stream wind speed.
 */
export const JetStreamColorExpression: Expression = [
  "interpolate",
  ["linear"],
  ["to-number", ["get", "wind_speed"], 0],
  60,
  "#6fa8dc",
  90,
  "#3d85c6",
  120,
  "#0b5394",
  150,
  "#38761d",
  180,
  "#f1c232",
  210,
  "#e69138",
  240,
  "#cc0000",
];

const JET_STREAM_LEGEND_STOPS: ColormapCreate["stops"] = [
  [60, "#6fa8dc"],
  [90, "#3d85c6"],
  [120, "#0b5394"],
  [150, "#38761d"],
  [180, "#f1c232"],
  [210, "#e69138"],
  [240, "#cc0000"],
  [270, "#cc0000"],
];

/**
 * Static legend metadata for the Jet Stream wind speed colormap.
 */
export const JetStreamLegendColormap: ColormapCreate = {
  type: "segmented",
  label: "Jet Stream Wind Speed",
  stops: JET_STREAM_LEGEND_STOPS,
};

const ARROW_HEAD_ANGLE_DEGREES = 30;
const ARROW_HEAD_LENGTH_FACTOR = 0.8;
const ARROW_HEAD_MIN_LENGTH_DEGREES = 0.06;
const ARROW_HEAD_MAX_LENGTH_DEGREES = 0.42;
const MIN_CHAIN_LENGTH_FOR_ARROW_DEGREES = 1.2;
const MIN_TIP_SEGMENT_LENGTH_FOR_ARROW_DEGREES = 0.22;

type JetStreamFeature = GeoJSON.Feature<GeoJSON.Geometry, Record<string, any>>;
type SegmentWithProps = { props: Record<string, any>; coordinates: Position[] };

/**
 * Type guard for valid coordinate tuples.
 */
function isValidPosition(position: unknown): position is Position {
  if (!Array.isArray(position) || position.length < 2) {
    return false;
  }
  return Number.isFinite(position[0]) && Number.isFinite(position[1]);
}

/**
 * Exact lon/lat equality for API-provided segment continuation points.
 */
function hasSameLonLat(first: Position, second: Position): boolean {
  return first[0] === second[0] && first[1] === second[1];
}

/**
 * Keeps only valid lon/lat positions from unknown coordinate data.
 */
function sanitizeLineCoordinates(line: unknown): Position[] {
  if (!Array.isArray(line)) {
    return [];
  }
  return line.filter(isValidPosition);
}

/**
 * Extracts all line segments from a feature (LineString or MultiLineString).
 */
function extractLineSegments(feature: GeoJSON.Feature): Position[][] {
  const geometry = feature.geometry;
  if (!geometry) {
    return [];
  }

  if (geometry.type === "LineString") {
    const line = sanitizeLineCoordinates((geometry as GeoJSON.LineString).coordinates);
    return line.length >= 2 ? [line] : [];
  }

  if (geometry.type === "MultiLineString") {
    const multi = (geometry as GeoJSON.MultiLineString).coordinates ?? [];
    return multi.map(sanitizeLineCoordinates).filter((line) => line.length >= 2);
  }

  return [];
}

/**
 * Type guard for features that contain at least one valid line segment.
 */
function isJetStreamLineFeature(feature: GeoJSON.Feature): feature is JetStreamFeature {
  return extractLineSegments(feature).length > 0;
}

/**
 * Creates two arrowhead wing segments at `head`, pointing from `tail` to `head`.
 */
function createArrowHeadSegments(tail: Position, head: Position, arrowSizeScale = 1): Position[][] {
  const avgLat = (((tail[1] ?? 0) + (head[1] ?? 0)) * Math.PI) / 360;
  const cosLat = Math.max(0.1, Math.cos(avgLat));
  const dx = ((tail[0] ?? 0) - (head[0] ?? 0)) * cosLat;
  const dy = (tail[1] ?? 0) - (head[1] ?? 0);
  const length = Math.hypot(dx, dy);

  if (!Number.isFinite(length) || length <= Number.EPSILON) {
    return [];
  }

  const ux = dx / length;
  const uy = dy / length;
  const wingLength = Math.min(
    ARROW_HEAD_MAX_LENGTH_DEGREES * arrowSizeScale,
    Math.max(ARROW_HEAD_MIN_LENGTH_DEGREES * arrowSizeScale, length * ARROW_HEAD_LENGTH_FACTOR * arrowSizeScale),
  );
  const angleRad = (ARROW_HEAD_ANGLE_DEGREES * Math.PI) / 180;

  const rotate = (x: number, y: number, angle: number): [number, number] => {
    return [x * Math.cos(angle) - y * Math.sin(angle), x * Math.sin(angle) + y * Math.cos(angle)];
  };

  const [leftX, leftY] = rotate(ux, uy, angleRad);
  const [rightX, rightY] = rotate(ux, uy, -angleRad);
  const left: Position = [(head[0] ?? 0) + leftX * (wingLength / cosLat), (head[1] ?? 0) + leftY * wingLength];
  const right: Position = [(head[0] ?? 0) + rightX * (wingLength / cosLat), (head[1] ?? 0) + rightY * wingLength];

  if (!isValidPosition(left) || !isValidPosition(right)) {
    return [];
  }

  return [
    [head, left],
    [head, right],
  ];
}

/**
 * Builds a local 2D vector with longitude scaled by latitude.
 */
function localVector(from: Position, to: Position): [number, number] {
  const avgLat = (((from[1] ?? 0) + (to[1] ?? 0)) * Math.PI) / 360;
  return [((to[0] ?? 0) - (from[0] ?? 0)) * Math.cos(avgLat), (to[1] ?? 0) - (from[1] ?? 0)];
}

/**
 * Euclidean length of a 2D vector.
 */
function vectorLength(vec: [number, number]): number {
  return Math.hypot(vec[0], vec[1]);
}

/**
 * Segment length in local degree space.
 */
function segmentLengthDegrees(from: Position, to: Position): number {
  return vectorLength(localVector(from, to));
}

/**
 * Polyline length in local degree space.
 */
function polylineLengthDegrees(chain: Position[]): number {
  let total = 0;
  for (let idx = 1; idx < chain.length; idx++) {
    total += segmentLengthDegrees(chain[idx - 1], chain[idx]);
  }
  return total;
}

/**
 * Chooses the segment endpoint for arrow placement.
 */
function pickHeadEndpoint(chain: Position[]): { head: Position; tail: Position } | null {
  if (chain.length < 2) {
    return null;
  }
  return {
    head: chain[chain.length - 1],
    tail: chain[chain.length - 2],
  };
}

/**
 * Converts raw jet stream features into render-ready lines:
 * backend-provided line segments and arrowhead features.
 */
export function normalizeJetStreamsFeatureCollection(
  fc: GeoJSONFeatureCollection,
  options?: { arrowSizeScale?: number },
): GeoJSONFeatureCollection {
  const arrowSizeScale = Math.max(0.4, Math.min(3, options?.arrowSizeScale ?? 1));
  const sourceSegments: SegmentWithProps[] = [];
  for (const feature of fc.features) {
    if (!isJetStreamLineFeature(feature)) {
      continue;
    }
    const segments = extractLineSegments(feature);
    const props = feature.properties ?? {};

    for (const segment of segments) {
      sourceSegments.push({
        props: { ...props },
        coordinates: [...segment],
      });
    }
  }

  const mergedFeatures: GeoJSON.Feature[] = [];

  for (const segment of sourceSegments) {
    mergedFeatures.push({
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: segment.coordinates,
      },
      properties: { ...segment.props },
    });
  }

  for (let segmentIdx = 0; segmentIdx < sourceSegments.length; segmentIdx++) {
    const segment = sourceSegments[segmentIdx];
    const chain = segment.coordinates;
    if (chain.length < 2) {
      continue;
    }
    const headSelection = pickHeadEndpoint(chain);
    if (!headSelection) {
      continue;
    }
    const { head, tail } = headSelection;
    const nextSegment = sourceSegments[segmentIdx + 1];
    const nextStart = nextSegment?.coordinates[0];
    const isContinuedByNextSegment = !!nextStart && hasSameLonLat(head, nextStart);
    const isBelowContinuedSegmentThreshold =
      polylineLengthDegrees(chain) < MIN_CHAIN_LENGTH_FOR_ARROW_DEGREES ||
      segmentLengthDegrees(tail, head) < MIN_TIP_SEGMENT_LENGTH_FOR_ARROW_DEGREES;
    if (isContinuedByNextSegment && isBelowContinuedSegmentThreshold) {
      continue;
    }

    const arrowHeadSegments = createArrowHeadSegments(tail, head, arrowSizeScale);
    for (const arrowCoordinates of arrowHeadSegments) {
      mergedFeatures.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: arrowCoordinates,
        },
        properties: {
          ...segment.props,
          _arrow_head: true,
        },
      });
    }
  }

  if (fc.features.length > 0 && mergedFeatures.length === 0) {
    // Safe fallback to avoid blank map if API geometry shape changes unexpectedly.
    return fc;
  }

  return {
    type: "FeatureCollection",
    features: mergedFeatures,
  };
}
